import React, { useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilSearch } from '@coreui/icons'
import { EmptyState, ErrorState, TileSkeleton, formatNumber } from '../AnalyticsV2/common'
import RankedBarList from '../AnalyticsV2/RankedBarList'
import { useChartTheme } from '../AnalyticsV2/chartTheme'
import { DeviceCell, deviceLabel, formatDateTime, timeAgo } from './platformHelpers'

const columns = [
  { key: 'full_name', label: 'Person', _style: { minWidth: 200 } },
  { key: 'game_name', label: 'Game', _style: { minWidth: 160 } },
  { key: 'device_type', label: 'Device', _style: { minWidth: 170 } },
  { key: 'current_screen', label: 'On screen' },
  { key: 'session_started_at', label: 'In app since' },
  { key: 'last_activity_at', label: 'Last action' },
].map((c) => ({ filter: false, ...c }))

const LivePanel = ({ live, onOpenUser, onOpenGame, onRefresh }) => {
  const theme = useChartTheme()
  const [gameFilter, setGameFilter] = useState(null)
  const [search, setSearch] = useState('')

  const people = useMemo(() => live.data?.people || [], [live.data])

  const games = useMemo(() => {
    const map = new Map()
    people.forEach((p) => {
      const g = map.get(p.game_id) || { game_id: p.game_id, game_name: p.game_name, users: 0 }
      g.users += 1
      map.set(p.game_id, g)
    })
    return [...map.values()].sort((a, b) => b.users - a.users)
  }, [people])

  const devices = useMemo(() => {
    const counts = {}
    people.forEach((p) => {
      counts[p.device_type || 'unknown'] = (counts[p.device_type || 'unknown'] || 0) + 1
    })
    return counts
  }, [people])

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return people.filter(
      (p) =>
        (!gameFilter || p.game_id === gameFilter) &&
        (!q ||
          (p.full_name || '').toLowerCase().includes(q) ||
          (p.email || '').toLowerCase().includes(q) ||
          (p.current_screen || '').toLowerCase().includes(q)),
    )
  }, [people, gameFilter, search])

  if (live.loading && !live.data) {
    return <TileSkeleton height={300} />
  }
  if (live.error && !live.data) {
    return (
      <CCard>
        <CCardBody>
          <ErrorState message={live.error} onRetry={onRefresh} />
        </CCardBody>
      </CCard>
    )
  }

  const d = live.data

  return (
    <>
      <CRow className="g-3 mb-3">
        <CCol xs={12} lg={4}>
          <CCard className="h-100 pa-live-card">
            <CCardBody>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="pa-live-dot" aria-hidden="true" />
                <span className="small fw-semibold text-uppercase pa-live-label">Live now</span>
                <CButton
                  color="secondary"
                  variant="ghost"
                  size="sm"
                  className="ms-auto"
                  onClick={onRefresh}
                  title="Refresh"
                >
                  <CIcon icon={cilReload} />
                </CButton>
              </div>
              <div className="pa-live-value">{formatNumber(d.online_users)}</div>
              <div className="text-medium-emphasis mb-3">
                people in the app across <strong className="text-body">{d.online_games}</strong>{' '}
                {d.online_games === 1 ? 'game' : 'games'}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {Object.entries(devices).map(([k, v]) => (
                  <span key={k} className="av2-chip">
                    {deviceLabel(k)}: <strong>{formatNumber(v)}</strong>
                  </span>
                ))}
              </div>
              <div className="small text-medium-emphasis mt-3">
                Active in the last {d.window_minutes} min · updated{' '}
                {new Date(d.as_of).toLocaleTimeString()} · refreshes every 30 s
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol xs={12} lg={8}>
          <CCard className="h-100">
            <CCardHeader>
              <strong>Games with people online</strong>
              <div className="small text-medium-emphasis">
                Click a game to filter the list below — click again to clear
              </div>
            </CCardHeader>
            <CCardBody className="pa-ranked-plain pa-scroll-box">
              <RankedBarList
                rows={games.map((g) => ({
                  key: g.game_id,
                  label: g.game_name,
                  sublabel: gameFilter === g.game_id ? 'Filtering ✓' : `#${g.game_id}`,
                  value: g.users,
                }))}
                color={theme.series[2]}
                valueLabel="online"
                emptyLabel="Nobody is in the app right now."
                onRowClick={(row) => setGameFilter(gameFilter === row.key ? null : row.key)}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardHeader className="d-flex flex-wrap align-items-center gap-2">
          <div className="me-auto">
            <strong>Who is online</strong>
            <div className="small text-medium-emphasis">
              {gameFilter
                ? `In ${games.find((g) => g.game_id === gameFilter)?.game_name || 'this game'}`
                : 'Every game'}{' '}
              · {formatNumber(rows.length)} shown
            </div>
          </div>
          {gameFilter && (
            <CButton
              color="link"
              size="sm"
              onClick={() =>
                onOpenGame({
                  id: gameFilter,
                  game_name: games.find((g) => g.game_id === gameFilter)?.game_name,
                })
              }
            >
              Game insights
            </CButton>
          )}
          <div className="position-relative pa-live-search">
            <CIcon icon={cilSearch} className="pa-search-icon text-medium-emphasis" />
            <CFormInput
              size="sm"
              className="ps-5"
              placeholder="Search person or screen"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search online people"
            />
          </div>
        </CCardHeader>
        <CCardBody>
          {people.length === 0 ? (
            <EmptyState
              height={200}
              title="Nobody is in the app right now"
              hint="This list refreshes every 30 seconds."
            />
          ) : (
            <div className="ge-datatable ge-datatable--no-select pa-games-table">
              <CSmartTable
                items={rows}
                columns={columns}
                columnSorter
                sorterValue={{ column: 'last_activity_at', state: 'desc' }}
                pagination
                itemsPerPage={25}
                noItemsLabel="Nobody online matches this search"
                clickableRows
                onRowClick={(item) => onOpenUser({ id: item.user_id, full_name: item.full_name })}
                tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                scopedColumns={{
                  full_name: (item) => (
                    <td>
                      <div className="d-flex align-items-center gap-2 fw-semibold">
                        <span className="pa-online-dot" aria-hidden="true" />
                        <span className="text-truncate pa-game-name">
                          {item.full_name || `User #${item.user_id}`}
                        </span>
                      </div>
                      <div className="small text-medium-emphasis text-truncate pa-game-name">
                        {item.email}
                      </div>
                    </td>
                  ),
                  game_name: (item) => (
                    <td>
                      <span className="text-truncate d-inline-block pa-game-name">
                        {item.game_name}
                      </span>
                    </td>
                  ),
                  device_type: (item) => (
                    <td>
                      <DeviceCell row={item} />
                    </td>
                  ),
                  current_screen: (item) => (
                    <td>
                      <span className="av2-screen-name small">{item.current_screen || '—'}</span>
                    </td>
                  ),
                  session_started_at: (item) => (
                    <td className="text-nowrap" title={formatDateTime(item.session_started_at)}>
                      {timeAgo(item.session_started_at) || '—'}
                    </td>
                  ),
                  last_activity_at: (item) => (
                    <td className="text-nowrap" title={formatDateTime(item.last_activity_at)}>
                      {timeAgo(item.last_activity_at) || '—'}
                    </td>
                  ),
                }}
              />
            </div>
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

export default LivePanel
